import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  ArrowUpRight,
  FileText,
  Search,
  RefreshCw,
  Building2,
  ShieldCheck,
  ClipboardCheck,
  Users,
  Database,
  Landmark,
  FileCheck2,
  BadgeCheck,
  Route,
  BriefcaseBusiness,
  FolderCheck,
  Layers3,
  UserCheck,
  ScrollText,
  Handshake,
  Archive,
  KeyRound,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";
const portalLink = "/portal/login?service=registry";

const focusAreas = [
  {
    title: "Enterprise Records",
    text: "Maintaining reliable registration records for Free Zone Enterprises, developers, operators and service providers.",
    icon: Database,
  },
  {
    title: "Licence Documentation",
    text: "Supporting licence records, renewals, amendments, corporate updates and official registry documentation.",
    icon: FileText,
  },
  {
    title: "Registry Verification",
    text: "Providing access for corporate search, registry confirmation and certified information requests.",
    icon: Search,
  },
];

const digitalServices = [
  {
    title: "Enterprise Registration",
    text: "For companies and investors seeking registration to operate within OGFZA-regulated Free Zones.",
    icon: Building2,
  },
  {
    title: "Licence Renewal",
    text: "For existing enterprises renewing or updating their registry and licence records.",
    icon: RefreshCw,
  },
  {
    title: "Company Record Update",
    text: "For updating directors, shareholding, address, contact details, activity profile and corporate information.",
    icon: FolderCheck,
  },
  {
    title: "Corporate Search",
    text: "For approved registry search, official confirmation and certified extract requests.",
    icon: Search,
  },
];

const serviceCoverage = [
  "New enterprise registration",
  "Licence record management",
  "Licence renewal support",
  "Corporate record update",
  "Registry verification",
  "Corporate search request",
  "Certified registry extract",
  "Registry status confirmation",
];

const registryPathway = [
  {
    title: "Identify",
    text: "Applicant selects the registry service category that matches the intended request.",
  },
  {
    title: "Submit",
    text: "Company details, supporting documents and application information are submitted through the portal.",
  },
  {
    title: "Verify",
    text: "Registry reviews submitted records for completeness, consistency and applicant category.",
  },
  {
    title: "Process",
    text: "Approved requests move through the required internal service route for review and decision.",
  },
  {
    title: "Issue",
    text: "Registry documents, confirmations or updates are issued and maintained in the official record.",
  },
];

const supportGroups = [
  {
    title: "New Investors",
    text: "For companies seeking to begin their Free Zone enterprise registration.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Existing Enterprises",
    text: "For licensed entities updating, renewing or verifying their records.",
    icon: UserCheck,
  },
  {
    title: "Developers & Promoters",
    text: "For organisations pursuing Free Zone development or promoter-related registration.",
    icon: Landmark,
  },
  {
    title: "Vendors & Providers",
    text: "For service providers seeking accreditation or registry documentation access.",
    icon: Handshake,
  },
];

const serviceHighlights = [
  {
    title: "Official Records",
    text: "Keeps enterprise records structured, traceable and accessible for authorised registry services.",
    icon: Archive,
  },
  {
    title: "Document Control",
    text: "Supports proper handling of registration documents, updates, renewals and corporate records.",
    icon: FileCheck2,
  },
  {
    title: "Verification Access",
    text: "Provides a clear path for approved corporate search and registry confirmation requests.",
    icon: BadgeCheck,
  },
  {
    title: "Investor Onboarding",
    text: "Guides qualified investors from registration interest to the appropriate digital service route.",
    icon: KeyRound,
  },
];

const Registry = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.2,
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 1,
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 55,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          delay: index * 0.05,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA Registry Service"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            OGFZA Service Area
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Registry Service
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Keeping investor records accurate, secure and accessible through
            enterprise registration, licence documentation, corporate updates
            and official registry verification.
          </p>

          <div className="hero-text flex flex-wrap gap-4 mt-10">
            <Link
              to={portalLink}
              className="inline-flex items-center gap-3 bg-[#F2AF11] text-[#082567] px-7 py-4 rounded-full font-black hover:bg-white transition-all duration-300"
            >
              Open Registry Portal
              <ArrowUpRight size={18} />
            </Link>

            <Link
              to="/contact-help/customer-service"
              className="inline-flex items-center gap-3 bg-white/10 border border-white/20 backdrop-blur-md text-white px-7 py-4 rounded-full font-black hover:bg-white hover:text-[#082567] transition-all duration-300"
            >
              Contact Support
              <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/services"
              className="hover:text-[#F2AF11] transition-colors"
            >
              Services
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Registry</span>
          </div>
        </div>
      </section>

      <section className="relative -mt-24 z-20 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {focusAreas.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate group bg-white rounded-[34px] p-8 border border-slate-200 shadow-[0_24px_80px_-50px_rgba(8,37,103,0.55)] hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500"
                >
                  <div className="w-16 h-16 rounded-[24px] bg-[#082567] text-white flex items-center justify-center mb-7 group-hover:bg-[#F2AF11] group-hover:text-[#082567] transition-all duration-500">
                    <Icon size={28} />
                  </div>

                  <h3
                    className="text-2xl font-black text-[#082567] mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 left-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.08fr_0.92fr] gap-16 items-center">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Service Overview
            </p>

            <h2
              className="text-4xl md:text-5xl font-black leading-tight text-[#082567] mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              The Official Records Gateway for Free Zone Enterprises
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <div className="space-y-5 text-slate-700 leading-[1.9] text-[16px]">
              <p>
                The Registry Service supports investors throughout the lifecycle
                of their Free Zone operations by maintaining enterprise records,
                processing company updates, managing licence documentation and
                enabling official verification services.
              </p>

              <p>
                This page introduces the Registry service area on the main
                website and directs users to the dedicated Registry Portal for
                registration, renewals, amendments, document submission and
                application tracking.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-9">
              <div className="rounded-[24px] bg-[#FAFBFC] border border-slate-200 p-6">
                <ScrollText className="text-[#026B07] mb-4" size={28} />
                <h3 className="font-black text-[#082567] text-lg">
                  Record Clarity
                </h3>
                <p className="text-slate-600 text-sm leading-[1.7] mt-2">
                  Clear guidance for registry-related service access.
                </p>
              </div>

              <div className="rounded-[24px] bg-[#082567] p-6 text-white">
                <Route className="text-[#F2AF11] mb-4" size={28} />
                <h3 className="font-black text-lg">Portal Workflow</h3>
                <p className="text-white/75 text-sm leading-[1.7] mt-2">
                  Central login with a dedicated Registry dashboard.
                </p>
              </div>
            </div>
          </div>

          <div className="relative reveal-up">
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-[#F2AF11] rounded-[30px]" />
            <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-[#026B07] rounded-full" />

            <div className="relative rounded-[38px] overflow-hidden bg-white border border-slate-200 p-3 shadow-[0_30px_90px_-55px_rgba(8,37,103,0.7)]">
              <img
                src={sectionBg}
                alt="OGFZA Registry Documentation"
                className="w-full h-[520px] object-cover rounded-[30px]"
              />

              <div className="absolute inset-3 rounded-[30px] bg-gradient-to-t from-[#041018]/80 via-[#041018]/10 to-transparent" />

              <div className="absolute bottom-8 left-8 right-8">
                <div className="inline-flex items-center gap-2 bg-white text-[#082567] px-5 py-3 rounded-full font-black shadow-lg">
                  <BadgeCheck size={18} className="text-[#026B07]" />
                  Enterprise Registration Support
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start">
            <div className="reveal-up lg:sticky lg:top-28">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Digital Access
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Registry Services Available Through the Portal
              </h2>

              <p className="text-slate-600 leading-[1.9] mt-6">
                The public website explains the Registry Service area. The
                portal manages registration applications, document upload,
                renewals, amendments, corporate search and service tracking.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {digitalServices.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="card-animate rounded-[32px] bg-white p-8 border border-slate-200 hover:-translate-y-2 hover:border-[#F2AF11]/70 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-6 border border-[#F2AF11]/25">
                      <Icon size={26} />
                    </div>

                    <h3
                      className="text-2xl font-black text-[#082567] leading-tight mb-4"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-[#082567]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-0 rounded-[38px] overflow-hidden shadow-[0_30px_90px_-60px_rgba(8,37,103,0.7)]">
            <div className="bg-white p-8 md:p-12 grid sm:grid-cols-2 gap-4">
              {serviceCoverage.map((item, index) => (
                <div
                  key={index}
                  className="card-animate rounded-[24px] border border-slate-200 p-5 hover:border-[#F2AF11]/70 hover:-translate-y-1 transition-all duration-500"
                >
                  <FileText className="text-[#026B07] mb-4" size={24} />
                  <p className="text-[#082567] font-black leading-tight">
                    {item}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-[#082567] text-white p-8 md:p-12">
              <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-5">
                Service Coverage
              </p>

              <h2
                className="text-4xl md:text-5xl font-black leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Built for Investor Onboarding and Record Maintenance
              </h2>

              <p className="text-white/75 leading-[1.9] mt-6">
                Registry support applies to new investors, existing licensed
                enterprises, developers, promoters, vendors and service
                providers that require official records, updates, renewals or
                verification.
              </p>

              <Link
                to={portalLink}
                className="inline-flex items-center gap-3 bg-[#F2AF11] text-[#082567] px-7 py-4 rounded-full font-black hover:bg-white transition-all duration-300 mt-8"
              >
                Continue to Portal
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC] overflow-hidden">
        <div className="absolute -top-24 left-0 w-[520px] h-[520px] bg-[#F2AF11]/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl reveal-up mb-16">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Service Highlights
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Supporting Registration, Verification and Record Integrity
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceHighlights.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate group rounded-[30px] bg-white p-7 border border-slate-200 hover:-translate-y-2 hover:border-[#F2AF11]/70 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center mb-6 group-hover:bg-[#F2AF11] group-hover:text-[#082567] transition-all duration-500">
                    <Icon size={25} />
                  </div>

                  <h3
                    className="text-xl font-black text-[#082567] leading-tight mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-[1.8] text-[15px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="OGFZA Registry Support"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.06]"
        />
        <div className="absolute inset-0 bg-white/94" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl reveal-up mb-16">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Investor Pathway
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              From Registry Request to Official Record Update
            </h2>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-[42px] left-0 right-0 h-1 bg-slate-200" />

            <div className="grid md:grid-cols-5 gap-6">
              {registryPathway.map((step, index) => (
                <div key={index} className="card-animate relative">
                  <div className="w-20 h-20 rounded-full bg-[#082567] text-white border-[6px] border-white flex items-center justify-center font-black text-xl mb-6 relative z-10">
                    {index + 1}
                  </div>

                  <div className="rounded-[28px] bg-white border border-slate-200 p-6 h-full hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500">
                    <h3
                      className="text-2xl font-black text-[#082567] mb-3"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {step.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-sm">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-center">
            <div className="reveal-up">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Who This Supports
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                For Investors, Enterprises, Developers and Service Providers
              </h2>

              <p className="text-slate-600 leading-[1.9] mt-6">
                Registry support is relevant to organisations seeking
                enterprise registration, licence renewal, corporate update,
                registry search, official confirmation or document verification
                within the Oil and Gas Free Zone ecosystem.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {supportGroups.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="card-animate rounded-[28px] bg-white border border-slate-200 p-7 hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-5">
                      <Icon size={24} />
                    </div>

                    <h3
                      className="text-xl font-black text-[#082567] leading-tight mb-3"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.7] text-sm">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[40px] bg-[#082567] p-8 md:p-12 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
            <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/20 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#026B07]/30 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:70px_70px]" />

            <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div className="reveal-up">
                <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  Central Portal Access
                </p>

                <h2
                  className="text-4xl md:text-5xl font-black text-white leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Access the Registry Dashboard Through the OGFZA Portal
                </h2>

                <p className="text-white/75 leading-[1.9] mt-5 max-w-3xl">
                  All service areas use one central login. After authentication,
                  Registry users are directed to the Registry dashboard for
                  enterprise registration, renewals, amendments, corporate
                  search, record updates and application tracking.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-4">
                <Link
                  to={portalLink}
                  className="inline-flex items-center justify-center gap-3 bg-[#F2AF11] text-[#082567] px-8 py-4 rounded-full font-black hover:bg-white transition-all duration-300"
                >
                  Open Registry Portal
                  <ArrowUpRight size={18} />
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-3 bg-white/10 border border-white/20 text-white px-8 py-4 rounded-full font-black hover:bg-white hover:text-[#082567] transition-all duration-300"
                >
                  View Other Services
                  <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Registry;